/* ══════════════════════════════════════════════════════════════
   Modulo — shared header pin engine
   Used by BOTH `section-header-pill` and `section-header-commerce`;
   edit it with both siblings in mind.

   A sticky header is two components in series and they fail differently:
   the section's own JS toggles a class, and CSS pins the bar with
   `position: fixed`. This file owns everything that makes the SECOND half
   survive a real merchant theme, because that is where every reported
   "the header is still not sticky" has actually lived:

     - `position: fixed` only resolves against the viewport while no ancestor
       is a containing block for fixed descendants, and a theme owns two of
       the three wrappers our headers sit inside
     - the offender can arrive AFTER the first stick, so the check cannot be
       one-shot, and the repair cannot be an inline style a theme can rewrite
     - a theme that scrolls an inner container leaves pageYOffset at 0, so the
       class is never applied at all and there is nothing to repair
     - the bar can be laid out perfectly and painted nowhere, clipped by an
       ancestor's overflow or buried under a higher stacking order

   Every branch is EMPIRICAL: it measures the failure and stops the moment the
   measurement comes good, so the blast radius stays one property on one
   ancestor rather than a blanket reset of the theme's boxes.

   Pinned by scripts/probe-header-pill-sticky.mjs (112 checks; 46 fail with the
   repair disabled via --control).
   ══════════════════════════════════════════════════════════════ */
(function () {
  if (window.moduloHeaderPin) return;
  var PIN_SEQ = 0;

  /**
   * @param root      the section's own root element
   * @param opts.head the element carrying `position: fixed`
   * @param opts.bar  the visible bar, used for the paint checks
   * @param opts.wrap selector for the wrapper that owns our stacking context
   * @param opts.cleanup array to push undo functions onto (optional)
   */
  window.moduloHeaderPin = function (root, opts) {
    opts = opts || {};
    var head = opts.head || root;
    var bar = opts.bar || head;
    var wrapSel = opts.wrap || '.shopify-section';
    var cleanup = opts.cleanup || [];

    /* ── transform-proof fixed ───────────────────────────────
       `position: fixed` resolves against the viewport ONLY while no
       ancestor is a containing block for fixed descendants. transform,
       perspective, filter, backdrop-filter, will-change and contain each
       make one — and this header sits inside two wrappers we do not
       author (`.shopify-section`, `#shopify-section-group-header-group`)
       plus <body>. A theme that sets any of those on either wrapper turns
       the stuck bar into an absolutely-positioned box that scrolls away
       with the page: the class is on, our CSS is correct, and the header
       simply is not sticky, with nothing in the settings to explain it.
       Measured across both headers; see the probe for the full matrix
       (scripts/probe-header-pill-sticky.mjs).

       Detection is EMPIRICAL rather than a property allowlist: a probe at
       `position:fixed; top:0; bottom:0` reports its containing block's box,
       so comparing that to the viewport catches whatever caused it — a
       property this list has not heard of included. Then neutralise the
       nearest offender, re-probe, and stop the moment the pin works, so
       the blast radius is one property on one ancestor rather than a
       blanket reset of the theme's wrappers. Restored on unload. */
    var NEUTRAL = {
      'transform': 'none',
      'perspective': 'none',
      'filter': 'none',
      'backdrop-filter': 'none',
      '-webkit-backdrop-filter': 'none',
      'will-change': 'auto',
      'contain': 'none',
      'content-visibility': 'visible',
      'transform-style': 'flat',
      'offset-path': 'none',
      'translate': 'none',
      'rotate': 'none',
      'scale': 'none',
      'animation-name': 'none',
      'overflow': 'visible'
    };

    function fixedIsBroken() {
      var probe = document.createElement('div');
      // clientHeight, not innerHeight: it already excludes a horizontal
      // scrollbar, which is exactly what the probe's own box excludes.
      probe.style.cssText = 'position:fixed;top:0;bottom:0;left:0;width:1px;visibility:hidden;pointer-events:none;';
      root.appendChild(probe);
      var r = probe.getBoundingClientRect();
      probe.parentNode.removeChild(probe);
      return Math.abs(r.top) > 1 ||
             Math.abs(r.height - document.documentElement.clientHeight) > 1;
    }

    function offendersOn(node) {
      var cs = window.getComputedStyle(node);
      var bad = [];
      if (cs.transform && cs.transform !== 'none') bad.push('transform');
      if (cs.perspective && cs.perspective !== 'none') bad.push('perspective');
      if (cs.filter && cs.filter !== 'none') bad.push('filter');
      if (cs.backdropFilter && cs.backdropFilter !== 'none') bad.push('backdrop-filter');
      if (cs.webkitBackdropFilter && cs.webkitBackdropFilter !== 'none') bad.push('-webkit-backdrop-filter');
      if (cs.willChange && /transform|translate|rotate|scale|perspective|filter|contain/.test(cs.willChange)) bad.push('will-change');
      if (cs.contain && /paint|layout|strict|content/.test(cs.contain)) bad.push('contain');
      // The INDEPENDENT transform properties are separate computed values, so
      // reading `transform` alone cannot see them: measured, each of
      // `translate`, `rotate` and `scale` creates a containing block on its own
      // while `getComputedStyle(node).transform` still reads "none".
      if (cs.translate && cs.translate !== 'none') bad.push('translate');
      if (cs.rotate && cs.rotate !== 'none') bad.push('rotate');
      if (cs.scale && cs.scale !== 'none') bad.push('scale');
      // A RUNNING transform animation keeps the containing block alive in
      // Chromium even after `transform: none !important` wins the cascade, so
      // the animation itself has to stop. Gated on the node actually carrying a
      // transform right now, so an unrelated fade is never touched.
      if (cs.animationName && cs.animationName !== 'none' &&
          ((cs.transform && cs.transform !== 'none') || (cs.translate && cs.translate !== 'none') ||
           (cs.rotate && cs.rotate !== 'none') || (cs.scale && cs.scale !== 'none'))) bad.push('animation-name');
      // content-visibility applies containment WITHOUT changing the computed
      // `contain` property, so the test above cannot see it: a theme that sets
      // `content-visibility: auto` on a wrapper (a common perf tweak) creates a
      // containing block while `getComputedStyle(node).contain` still reads
      // "none". Measured: the walk went straight past it and the bar scrolled
      // away with the page.
      if (cs.contentVisibility && cs.contentVisibility !== 'visible') bad.push('content-visibility');
      // Both measured breaking the pin with computed `transform` still "none",
      // so reading `transform` alone can never see either of them.
      if (cs.transformStyle && cs.transformStyle !== 'flat') bad.push('transform-style');
      if (cs.offsetPath && cs.offsetPath !== 'none') bad.push('offset-path');
      return bad;
    }

    /* Neutralise from a stylesheet !important rule rather than an inline style.
       Cascade order puts an IMPORTANT author declaration above a NORMAL inline
       one, so this also survives a theme whose own scroll JS keeps rewriting
       `element.style.transform` on the wrapper — an inline repair loses that
       fight every frame, and the merchant sees a header that sticks for one
       frame and then scrolls away. */
    var pinSheet = null;
    function neutralise(node, props) {
      if (!pinSheet) {
        pinSheet = document.createElement('style');
        (document.head || document.documentElement).appendChild(pinSheet);
        cleanup.push(function () {
          if (pinSheet && pinSheet.parentNode) pinSheet.parentNode.removeChild(pinSheet);
          pinSheet = null;
        });
      }
      var body = [];
      for (var i = 0; i < props.length; i++) body.push(props[i] + ':' + NEUTRAL[props[i]] + ' !important');
      return forceDecls(node, body.join(';'));
    }

    function forceDecls(node, decls) {
      if (!pinSheet) {
        pinSheet = document.createElement('style');
        (document.head || document.documentElement).appendChild(pinSheet);
        cleanup.push(function () {
          if (pinSheet && pinSheet.parentNode) pinSheet.parentNode.removeChild(pinSheet);
          pinSheet = null;
        });
      }
      var cls = 'modulo-pin-fix-' + (++PIN_SEQ);
      node.classList.add(cls);
      var undo = function () { node.classList.remove(cls); };
      cleanup.push(undo);
      pinSheet.appendChild(document.createTextNode('.' + cls + '{' + decls + '}'));
      return undo;
    }

    var pinChecked = false, pinRepairs = 0;
    function ensurePinWorks() {
      if (pinChecked) return;
      pinChecked = true;
      if (!fixedIsBroken()) return;

      var node = root;
      while (node && node.nodeType === 1) {
        var bad = offendersOn(node);
        if (bad.length) {
          neutralise(node, bad);
          if (!fixedIsBroken()) return;
        }
        if (node === document.documentElement) break;
        node = node.parentNode;
      }

      /* The walk above is still an allowlist, and this list has now been wrong
         twice in production. If the pin is broken and nothing recognised it, try
         the WHOLE neutral set on each ancestor and keep only what measurably
         helps, so a property nobody here has heard of gets repaired rather than
         shrugged at. Rolled back per node when it does not help, which keeps the
         blast radius identical to the targeted path. */
      if (!fixedIsBroken()) return;
      var all = [];
      for (var k in NEUTRAL) if (Object.prototype.hasOwnProperty.call(NEUTRAL, k)) all.push(k);
      node = root;
      while (node && node.nodeType === 1) {
        var undo = neutralise(node, all);
        if (!fixedIsBroken()) return;
        undo();
        if (node === document.documentElement) break;
        node = node.parentNode;
      }
    }

    /* ── overlay: the bar has to be VISIBLE over the hero ─────────
       In overlay mode the head is `position: absolute` inside a wrapper that
       collapses to ZERO height, so a theme's `overflow: hidden` on
       `.shopify-section` or on the header group clips it out of existence.
       The moment it goes fixed it escapes that clip, so what the merchant sees
       is a header that is missing on the hero and then appears once they
       scroll — which reads as "it is not sitting on my hero image".
       Measured: bar present, correctly placed, painting nothing.

       Bounded on purpose — the walk stops at the header group and never
       reaches <body>, whose `overflow-x: hidden` is a deliberate mobile guard
       we must not remove. */
    function barHidden() {
      if (!bar) return false;
      var r = bar.getBoundingClientRect();
      if (!r.width || !r.height) return false;
      var x = Math.round(r.left + r.width / 2);
      var y = Math.round(r.top + r.height / 2);
      if (x < 0 || y < 0 || y > document.documentElement.clientHeight) return false;
      var hit = document.elementFromPoint(x, y);
      return !!hit && !root.contains(hit);
    }

    /* Deliberately NOT gated on overlay mode: a pinned solid header is buried
       by a high z-index exactly the same way, and every branch here is already
       gated on the bar being PROVABLY invisible, so it costs nothing when the
       header is fine. */
    var visTries = 0;
    function ensureBarVisible() {
      if (visTries >= 3 || !barHidden()) return;
      visTries++;

      // 1. a clipping ancestor (overlay's zero-height containing block).
      var node = root.parentNode;
      while (node && node.nodeType === 1 && node !== document.body) {
        var cs = window.getComputedStyle(node);
        if (cs.overflowX !== 'visible' || cs.overflowY !== 'visible') {
          neutralise(node, ['overflow']);
          if (!barHidden()) return;
        }
        if (node.id === 'shopify-section-group-header-group') break;
        node = node.parentNode;
      }

      // 2. still buried: something paints above us. `z-index: 60` on our
      //    wrapper is a guess about the theme, so read what actually won and
      //    go one better rather than picking another magic number.
      var r = bar.getBoundingClientRect();
      var hit = document.elementFromPoint(Math.round(r.left + r.width / 2), Math.round(r.top + r.height / 2));
      var z = 0, n = hit;
      while (n && n.nodeType === 1 && n !== document.documentElement) {
        var v = parseInt(window.getComputedStyle(n).zIndex, 10);
        if (!isNaN(v) && v > z) z = v;
        n = n.parentNode;
      }
      if (!z) return;
      var wrap = root.closest ? root.closest(wrapSel) : null;
      forceDecls(wrap || root.parentNode || root, 'position:relative;z-index:' + (z + 1) + ' !important');
    }


    /* The scroll position has to come from whatever is actually scrolling.
       When a theme (or a smooth-scroll library, or a drawer that locks the
       page) puts the page inside an inner scroll container, pageYOffset stays
       0 for the whole session, `stuck` is never true and the bar never pins —
       with the setting on, the CSS correct and nothing to see in the settings. */
    var altScroller = null;
    function findScroller() {
      var node = root.parentNode;
      while (node && node.nodeType === 1 && node !== document.body) {
        var cs = window.getComputedStyle(node);
        if (/(auto|scroll)/.test(cs.overflowY) && node.scrollHeight > node.clientHeight + 1) return node;
        node = node.parentNode;
      }
      return null;
    }
    function scrollY() {
      var y = window.pageYOffset || document.documentElement.scrollTop || 0;
      if (y) return y;
      if (!altScroller) altScroller = findScroller();
      return altScroller ? altScroller.scrollTop : 0;
    }


    return {
      scrollY: scrollY,
      findScroller: findScroller,
      ensureVisible: ensureBarVisible,
      /* Call on every tick while stuck. `isHidden` is the section's own
         scroll-up state: it translates the head off screen legitimately, so
         the slip detector must not read that as a broken pin. */
      onStick: function (isHidden) {
        if (pinChecked && pinRepairs < 5 && !isHidden &&
            head && head.getBoundingClientRect().top < -1) {
          pinChecked = false;
          pinRepairs++;
        }
        ensurePinWorks();
      }
    };
  };
})();
